<template>
    <edit-resource
        :form="form"
        :title="title"
        @close="$emit('close')"
        @submit="saveTask"
    >
        <div>
            <div class="flex items-center mb-16">
                <span class="mr-8 first-letter:uppercase">{{ $t('tasks.assign_to') }}</span>
                <user-selector v-model="form.assignee_id" />
            </div>
            <simple-input class="mb-16" v-model="form.title">{{ $t('tasks.title') }}</simple-input>

            <simple-textarea class="mb-16" v-model="form.notes">{{ $t('tasks.notes') }}</simple-textarea>

            <date-picker
                v-model="form.due_date"
                time
                class="mb-16"
            >{{ $t('tasks.due_date') }}</date-picker>

            <checkbox v-model="form.private">
                {{ $t('tasks.private') }}
            </checkbox>
        </div>
    </edit-resource>
</template>

<script>
import EditResource from './EditResource.vue'
import Form from '../../etc/forms/Form';
import Task from '../../models/Task';
import Input from '../forms/Input.vue';
import TextArea from '../forms/TextArea.vue';
import * as tasks from '../../services/TaskService';
import DatePicker from '../forms/DatePicker.vue';
import moment from 'moment';
import { normalizeJson } from '../../../shared/helpers';
import UserSelector from './UserSelector.vue';
import {mapState, mapActions} from 'pinia';
import {useRepo} from 'pinia-orm';
import {useMainStore} from '../../state/store';
import {useAuthenticationStore} from '../../state/modules/authentication';
import {useNotificationsStore} from '../../state/modules/notifications';
import { ADD_NOTIFICATION } from '../../state/mutation-types';
import Application from '../../models/Application';
import Checkbox from "../forms/Checkbox";

export default {
    components: {
        Checkbox,
        'edit-resource': EditResource,
        'simple-input': Input,
        'simple-textarea': TextArea,
        'date-picker': DatePicker,
        'user-selector': UserSelector,
    },
    props: {
        task: Task,
        title: String
    },
    setup() {
        const authStore = useAuthenticationStore();
        return {authStore};
    },
    data: function () {
        return {
            form: this.generateForm(),
        }
    },
    computed: {
        ...mapState(useMainStore, ['tasks']),
        ...mapState(useAuthenticationStore, ['currentUserId']),
    },
    methods: {
        ...mapActions(useNotificationsStore, [ADD_NOTIFICATION]),
        generateForm () {
            let fields = [
                { name: 'title', value: '', },
                { name: 'notes', value: '', },
                { name: 'due_date', value: null, accessor: value => value ? moment.utc(value) : null },
                { name: 'assignee_id', value: this.authStore.currentUserId },
                { name: 'private', value: false }
            ];

            if (this.task) {
                fields.forEach((field, index) => {
                    fields[index].value = JSON.parse(JSON.stringify(this.task[field.name]));
                });
            }

            return new Form(fields);
        },
        async saveTask () {
            if (this.task && this.task.id) {
                await this.updateTask();
            } else {
                await this.createTask();
            }

            this.$emit('close');
        },
        updateTask () {
            return this.form.submit(data => tasks.update(this.task.id, {
                data: {
                    attributes: {
                        title: data.title,
                        notes: data.notes,
                        due_date: data.due_date,
                        private: data.private
                    },
                    relationships: { assignee: { data: {
                        id: data.assignee_id
                    }}}
                }
            })).then(result => {
                return useRepo(Task).save(normalizeJson(result));
            }).then(inserts => {
                this[ADD_NOTIFICATION]({
                    type: 'success',
                    message: this.$t('tasks.update_success'),
                });
            });
        },
        createTask () {
            let subject = this.tasks.addingContext
                ? { subject: { data: {
                        type: this.tasks.addingContext.constructor.entity.slice(0, -1),
                        id: this.tasks.addingContext.id
                    }}}
                : {};
            return this.form.submit(data => tasks.store({
                data: {
                    attributes: {
                        title: data.title,
                        notes: data.notes,
                        due_date: data.due_date,
                        private: data.private
                    },
                    relationships: {
                        assignee: { data: { id: data.assignee_id }},
                        ...subject
                    }
                }
            })).then(result => {
                return useRepo(Task).save(normalizeJson(result));
            }).then(result => {
                if (result.subject_type === 'application') {
                    const applications = useRepo(Application)
                        .where('id', result.subject_id)
                        .get()
                        .map(application => ({
                            id: application.id,
                            tasks_count: application.tasks_count + 1
                        }));
                    useRepo(Application).save(applications);
                }

                window.bus.dispatch('task-added', {
                    context: this.tasks.addingContext,
                    assignee: result.assignee_id
                });

                this[ADD_NOTIFICATION]({
                    type: 'success',
                    message: this.$t('tasks.create_success'),
                });
            });
        }
    }
}
</script>
